import React, { Fragment, useState } from "react";
import {
  Step as MuiStep,
  StepLabel,
  StepContent,
  Typography,
  Button,
  IconButton,
  Collapse,
  Box,
  Stack,
  Divider,
  CircularProgress,
  ListItem,
  ListItemText,
  List,
  Avatar,
  ImageList,
  ImageListItem,
  TableRow,
  TableCell,
  Table,
  TableHead,
  TableBody,
  TableContainer,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useTheme } from "@emotion/react";
import { Delete } from "@mui/icons-material";
import DeleteIconButton from "../DeleteIconButton";
import { deleteDocReferencedImage } from "../../libs/firebase/cloudStorage/deleteDocReferencedImage";

const Step = ({
  stepNumber,
  stepName,
  isDone,
  stepIcon,
  stepHeading,
  stepSubtitle,
  doneStepHeading,
  doneStepSubtitle,
  stepActions,
  onSave,
  stepData,
  database,
  activeStep,
  onActiveStep,
  onNextStep,
  onClose,
  units,
  canChange = true,
  canGoNext = true,
}) => {
  const [newData, setNewData] = useState(null);
  const [isSaving, setSaving] = useState(false);
  const [isChanging, setChanging] = useState(false);
  const [deletedData, setDeletedData] = useState([]);

  isDone = isDone ? isChanging === false : false;

  const theme = useTheme();
  const isXsBreakPoint = useMediaQuery(theme.breakpoints.down("sm"));

  const cols = isXsBreakPoint ? 1 : 2;

  const handleToggleCollapse = () => {
    if (activeStep) {
      onClose();
    } else {
      onActiveStep();
    }
  };
  //   <MuiStep key={stepNumber}> completed={isDone}>
  // console.log("stepNumber", stepNumber);
  // console.log("stepName", stepName);
  // console.log("stepHeading", stepHeading);
  // console.log("stepSubtitle", stepSubtitle);
  // console.log("stepActions", stepActions);

  const Child = stepActions.children;
  const Result = stepActions.result;

  const handleDone = (stuff) => {
    console.log("Done", stuff);
  };

  /**
   * Logic to be implemented:
   *     - Step is done status should show a checkmark
   *     - Steps should be saved online
   *           - need to figure out the format
   *     - Save button should be disabled until something has changed
   */

  const handleChanged = (data) => {
    // setChanged(true);
    setNewData(data);
  };

  const handleSaved = () => {
    setSaving(false);
    setChanging(false);
  };

  const handleDeleteImage = (index) => {
    console.log("handleDeleteImage", index);
    // New image array
    let newData = [...stepData];
    // Remove the image from the array
    console.log("sliced");

    setDeletedData([...deletedData, ...newData.splice(index, 1)]);

    setNewData(newData);
  };

  const isDeletedData = (image) => {
    return deletedData.includes(image);
  };

  // turn the stepData into results to show
  // if step data is an array then show the array as an MUI List
  // if step data is an object then show the object as a table
  // if step data is a string then show the string as a paragraph
  let stepDataAsResults = null;

  if (stepData) {
    if (typeof stepData === "number") {
      // show as number
      stepDataAsResults = (
        <Typography variant="body1">
          {stepData}
          {units}
        </Typography>
      );
    } else {
      if (
        Array.isArray(stepData) ||
        (typeof stepData === "object" && stepData.images)
      ) {
        let images = stepData.images || stepData;
        // check the first string is url
        if (typeof images[0] === "string" && images[0].includes("http")) {
          // show as images
          //masonry list
          stepDataAsResults = (
            <ImageList
              variant="masonry"
              cols={cols}
              gap={8}
              style={{
                marginBottom: "0",
              }}
            >
              {images.map((item, index) => (
                <ImageListItem
                  key={index}
                  style={{
                    //greyscale filter
                    filter: isDeletedData(item) ? "grayscale(1)" : "none",
                  }}
                >
                  {isChanging && !isDeletedData(item) && (
                    <DeleteIconButton
                      style={{
                        position: "absolute",
                        top: "0",
                        right: "0",
                      }}
                      name="image"
                      color="secondary"
                      onConfirm={() => {
                        handleDeleteImage(index);
                      }}
                    />
                  )}
                  <img
                    srcSet={`${item}?w=248&fit=crop&auto=format&dpr=2 2x`}
                    src={`${item}?w=248&fit=crop&auto=format`}
                    // alt={item}
                    loading="lazy"
                    style={{
                      borderRadius: "1rem",
                    }}
                  />
                </ImageListItem>
              ))}
            </ImageList>
          );
        } else if (typeof stepData[0] === "object" && !stepData[0].image) {
          // show as table where the objects properties are the table headers
          let stepResults = stepData.map((item, index) => {
            let itemKeys = Object.keys(item);
            let itemValues = Object.values(item);

            // if any values are an object with seconds and nanoseconds then convert to date
            itemValues = itemValues.map((value) => {
              if (typeof value === "object") {
                if (value.seconds && value.nanoseconds) {
                  return new Date(value.seconds * 1000).toLocaleString();
                }
              }
              // if empty string then return N/A
              if (value === "") {
                return "N/A";
              }
              return value;
            });
            return (
              <TableRow key={index}>
                {itemKeys.map((key, index) => (
                  <TableCell key={index}>{itemValues[index]}</TableCell>
                ))}
              </TableRow>
            );
          });
          stepDataAsResults = (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {Object.keys(stepData[0]).map((key, index) => (
                      <TableCell key={index}>{key}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>{stepResults}</TableBody>
              </Table>
            </TableContainer>
          );
        } else {
          // show as list
          let stepResults = images.map((item, index) => (
            <ListItem key={index}>
              <Stack spacing={2} direction={"row"}>
                {item.image ? (
                  <Avatar src={item.image} />
                ) : (
                  <div
                    style={{
                      backgroundColor: item.image || item.toLowerCase(),
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      marginRight: "8px",
                    }}
                  />
                )}
                <ListItemText
                  primary={item.name || item}
                  style={{
                    alignSelf: "center",
                  }}
                />
              </Stack>
            </ListItem>
          ));
          stepDataAsResults = <List>{stepResults}</List>;
        }
      } else if (typeof stepData === "object") {
        // show as table
        let stepResults = Object.keys(stepData).map((key, index) => {
          //change key text from camelCase to Title Case
          let keyText = key.replace(/([A-Z])/g, " $1");
          keyText = keyText.charAt(0).toUpperCase() + keyText.slice(1);

          return (
            <ListItem
              key={index}
              // dot list
              style={{
                display: "list-item",
              }}
            >
              <ListItemText
                primary={keyText}
                secondary={stepData[key]}
                style={{
                  alignSelf: "center",
                }}
              />
            </ListItem>
          );
        });
        stepDataAsResults = (
          <List
            // dot list
            style={{
              listStyleType: "disc",
            }}
          >
            {stepResults}
          </List>
        );
      } else if (typeof stepData === "string") {
        // show as paragraph
        stepDataAsResults = <Typography variant="body1">{stepData}</Typography>;
      }
    }
  }

  return (
    <MuiStep
      key={stepNumber}
      style={{
        boxShadow: "2px 2px 4px rgba(0, 0, 0, 0.25)",
        padding: "1rem",
        borderRadius: "1rem",
        marginBottom: "1rem",
        border: "1px solid #E0E0E0",
        // backgroundColor: "transparent",
        // "& .MuiStepConnector-line": {
        //     border: "1px solid #E0E0E0",
        // },
      }}
    >
      <StepLabel>
        <Box display="flex" alignItems="center">
          {isDone ? (
            <CheckCircleIcon color="secondary" />
          ) : (
            <RadioButtonUncheckedIcon color="secondary" />
          )}
          <Typography variant="subtitle1" sx={{ marginLeft: 1 }}>
            {stepName}
          </Typography>
          <IconButton
            onClick={handleToggleCollapse}
            size="small"
            sx={{ marginLeft: "auto" }}
          >
            <ExpandMoreIcon color="secondary" />
          </IconButton>
        </Box>
      </StepLabel>
      {/* <StepContent> */}
      <Collapse in={activeStep}>
        {/* divider */}
        <Divider
          sx={{
            my: 2,
          }}
        />
        {isSaving ? (
          <Box
            style={{
              textAlign: "center",
              margin: "2rem 0rem",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box
            style={{
              padding: "1rem 0rem",
            }}
          >
            <Box display="flex" alignItems="center" flexDirection={"column"}>
              {
                stepIcon && stepIcon
                //   <img
                //     src={stepIcon}
                //     alt={`Step ${stepNumber}`}
                //     style={{ marginRight: "16px" }}
                //   />
              }
              <Box>
                <Typography variant="h6">
                  {isDone ? doneStepHeading : stepHeading}
                </Typography>
                <Typography variant="subtitle1">
                  {isDone ? doneStepSubtitle : stepSubtitle}
                </Typography>
                {Result ? (
                  <Box
                    style={{
                      padding: "2rem 0rem",
                    }}
                  >
                    <Result data={stepData} />
                  </Box>
                ) : (
                  stepDataAsResults
                )}
              </Box>
            </Box>
            {isDone ? (
              <Stack mt={2} spacing={2}>
                {canChange && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setChanging(true)}
                  >
                    Change
                  </Button>
                )}
                {canGoNext && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={onNextStep}
                  >
                    Go to the Next Step
                  </Button>
                )}
              </Stack>
            ) : (
              <Box mt={2}>
                {stepActions.actions && stepActions.actions.lenght > 0 && (
                  <Fragment>
                    <Typography variant="h6">{stepActions.heading}</Typography>
                    <ul>
                      {stepActions.actions.map((action, index) => (
                        <li key={index}>{action}</li>
                      ))}
                    </ul>
                  </Fragment>
                )}
                {stepActions.children && deletedData.length === 0 && (
                  <Child
                    onDone={handleDone}
                    data={stepData}
                    onChange={handleChanged}
                    database={database}
                  />
                )}
                <Stack mt={2} spacing={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setSaving(true);
                      onSave(newData, handleSaved, deletedData);
                    }}
                    style={{
                      width: "fit-content",
                      alignSelf: "flex-end",
                    }}
                    disabled={!newData}
                  >
                    Save
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>
        )}
      </Collapse>
      {/* </StepContent> */}
    </MuiStep>
  );
};

export default Step;
